import React from 'react'
import Button from 'components/Button'
import Condition from 'components/Condition'
import StyledLink from 'components/StyledLink'
import CandidatesIcon from '@mui/icons-material/SupervisedUserCircle'
import CreateIcon from '@mui/icons-material/Create'
import useStyles from 'scenes/Job/styles'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { JobClientActions_User$key } from '__generated__/JobClientActions_User.graphql'
import { JobClientActions_Job$key } from '__generated__/JobClientActions_Job.graphql'
import { useIsClient, useIsRealAdmin, useIsRecruiterOrAdmin } from 'hooks/user'
import { useTheme } from '@mui/material/styles'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'

interface IJobClientActionsProps {
  userFragmentRef: JobClientActions_User$key
  jobFragmentRef: JobClientActions_Job$key
}

export default function JobClientActions({ userFragmentRef, jobFragmentRef }: IJobClientActionsProps) {
  const { classes } = useStyles()
  const theme = useTheme()

  const isLargeDesktop = !useResponsiveBreakpoint(theme.breakpoints.values.xl)

  const user = useFragment(graphql`
    fragment JobClientActions_User on User {
      ...user_useIsClient
      ...user_useIsRecruiterOrAdmin
      ...user_useIsRealAdmin
    }
  `, userFragmentRef)
  const client = useIsClient(user)
  const recruiter = useIsRecruiterOrAdmin(user)
  const admin = useIsRealAdmin(user)

  const job = useFragment(graphql`
    fragment JobClientActions_Job on Job {
      slug
      firm {
        slug
        self
      }
    }
  `, jobFragmentRef)

  if (!job || !user) return null

  if ((client && job?.firm?.self) || recruiter || admin) {
    const clientActions = (
      <React.Fragment>
        <Button
          component={StyledLink}
          color="primary"
          style={{ marginRight: theme.spacing(2), marginBottom: theme.spacing(2), textDecoration: 'none' }}
          href={`/hire/potential?company=${job.firm?.slug}&job=${job.slug}`}
          data-cy="view-candidates"
          startIcon={<CandidatesIcon />}
        >
          View Candidates
        </Button>

        <br />

        <Button
          component={StyledLink}
          style={{ marginBottom: theme.spacing(2), textDecoration: 'none' }}
          href={`/hire/jobs/${job.slug}/details`}
          data-cy="edit-job"
          startIcon={<CreateIcon />}
        >
          Edit job details
        </Button>

        <br />

        <Button
          component={StyledLink}
          style={{ marginBottom: theme.spacing(2), textDecoration: 'none' }}
          href={`/hire/jobs/${job.slug}/sourcing`}
          data-cy="edit-job-sourcing"
          startIcon={<CreateIcon />}
        >
          Edit job sourcing
        </Button>

        <br />

        <Button
          component={StyledLink}
          style={{ marginBottom: theme.spacing(2), textDecoration: 'none' }}
          href={`/hire/jobs/${job.slug}/screening`}
          data-cy="edit-job-screening"
          startIcon={<CreateIcon />}
        >
          Edit job screening
        </Button>

        <br />

        <Button
          component={StyledLink}
          style={{ marginBottom: theme.spacing(2), textDecoration: 'none' }}
          href={`/hire/jobs/${job.slug}/automated_messaging`}
          data-cy="edit-job-messaging"
          startIcon={<CreateIcon />}
        >
          Edit job messaging
        </Button>
      </React.Fragment>
    )

    return (
      <React.Fragment>
        <Condition condition={isLargeDesktop}>
          <div className={classes.sidebar}>
            {clientActions}
          </div>
        </Condition>

        <Condition condition={!isLargeDesktop}>
          <div className={classes.box}>
            {clientActions}
          </div>
        </Condition>
      </React.Fragment>
    )
  }

  return null
}
